import React from 'react';
import {
  Pressable,
  Text,
  Icon,
  Checkbox,
  CheckboxIndicator,
  CheckboxIcon,
  CheckIcon,
  HStack,
  Tooltip,
  Button,
  TooltipContent,
  TooltipText,
  Menu,
  MenuItem,
  MenuItemLabel,
  Divider,
} from '@gluestack-ui/themed';
import { ChevronUp, ChevronDown, MoreHorizontal } from 'lucide-react-native';
import { getFieldValue } from '../utils';
import { StatusBadge } from '../../StatusBadge';
import { AdminModelMap, AdminObjectName, ColumnDefinition } from '../../DataColumnConfig';
import { ActionConfig } from '../actionTypes';

/**
 * The same Gluestack menu as the user menu: a plain pressable trigger,
 * themed items, and a divider before a destructive action.
 * An item uses the standard `disabled` prop while an action is running or its `disabledCondition` is true.
 */
function RowMenu({
  menuActions,
  row,
  isActionRunning,
  onAction,
}: {
  menuActions: [string, ActionConfig][];
  row: unknown;
  isActionRunning: boolean;
  onAction?: (actionCode: string, data: unknown) => void;
}) {
  const visible = menuActions.filter(([, action]) => (
    action.availCondition ? action.availCondition(row) : true
  ));
  if (visible.length === 0) return null;

  const regular = visible.filter(([code]) => code !== 'delete' && code !== 'deleteSubmission');
  const destructive = visible.filter(([code]) => code === 'delete' || code === 'deleteSubmission');
  const itemDisabled = (action: ActionConfig) => (
    isActionRunning || Boolean(action.disabledCondition?.(row))
  );

  const item = (actionCode: string, action: ActionConfig) => {
    const isDelete = actionCode === 'delete' || actionCode === 'deleteSubmission';
    const unavailable = itemDisabled(action);
    return (
      <MenuItem
        key={actionCode}
        textValue={action.label || actionCode}
        onPress={() => onAction?.(actionCode, row)}
        disabled={unavailable}
        // Gluestack types the pressable `disabled` prop. The menu only greys
        // an item when the collection also receives `isDisabled`.
        {...({ isDisabled: unavailable } as object)}
      >
        {action.icon ? (
          <Icon
            as={action.icon as unknown as React.ComponentType}
            size="sm"
            mr="$2"
            color={isDelete ? '$statusError' : '$textSubtle'}
          />
        ) : null}
        <MenuItemLabel size="sm" color={isDelete ? '$statusError' : '$textBody'}>
          {action.label || actionCode}
        </MenuItemLabel>
      </MenuItem>
    );
  };

  return (
    <Menu
      placement="bottom right"
      trigger={({ ...triggerProps }) => (
        <Pressable
          {...triggerProps}
          p="$1"
          borderRadius="$full"
          accessibilityLabel="Row actions"
          testID="row-actions-menu"
          isDisabled={isActionRunning}
          $hover={{ bg: '$bgHover' }}
        >
          <Icon as={MoreHorizontal} size="sm" color="$textSubtle" />
        </Pressable>
      )}
    >
      {regular.map(([code, action]) => item(code, action))}
      {destructive.length > 0 && regular.length > 0 ? (
        <MenuItem key="row-actions-divider" textValue="separator" disabled closeOnSelect={false} p="$0">
          <Divider bg="$borderLight" w="$full" />
        </MenuItem>
      ) : null}
      {destructive.map(([code, action]) => item(code, action))}
    </Menu>
  );
}

interface UseDataTableColumnsProps<K extends AdminObjectName> {
  tableColumns: Record<string, ColumnDefinition<AdminModelMap[K]>>;
  includeDeprecated: boolean;
  handleSort: (field: string) => void;
  onSort?: (field: string | null, direction: 'asc' | 'desc' | null) => void;
  sortField: string | null;
  sortDirection: 'asc' | 'desc';
  singleLine: boolean;
  enableSelection: boolean;
  isSelectionMode: boolean;
  isActionRunning: boolean;
  onAction?: (actionCode: string, data: unknown, callbacks?: { onClearSelection: () => void; onExitSelectionMode: () => void }) => void;
  rowActions: [string, ActionConfig][];
  objectName: K;
}

/**
 * useDataTableColumns - A specialized hook to generate column definitions for TanStack Table.
 *
 * It separates the heavy logic of defining headers, cell renderers (checkboxes, status badges, dates),
 * and action buttons from the main component to keep the orchestrator file focused.
 *
 * It handles:
 * - Dynamic visibility of "Deprecated" columns.
 * - Sortable header components with visual indicators.
 * - Platform-agnostic cell rendering (using Gluestack UI components).
 * - Selection column injection.
 * - Row action button group generation with tooltips.
 *
 * @param {UseDataTableColumnsProps} props - Configuration for column generation.
 * @returns {any[]} An array of column definitions compatible with `useReactTable`.
 */
export const useDataTableColumns = <K extends AdminObjectName>({
  tableColumns,
  includeDeprecated,
  handleSort,
  onSort,
  sortField,
  sortDirection,
  singleLine,
  enableSelection,
  isSelectionMode,
  isActionRunning,
  onAction,
  rowActions,
}: UseDataTableColumnsProps<K>) => React.useMemo(() => {
    const activeKeys = Object.keys(tableColumns).filter((key) => {
      if (key === 'isDeprecated') return includeDeprecated;
      return true;
    });

    const defs: unknown[] = activeKeys.map((key) => ({
      id: key,
      accessorKey: key,
      size: tableColumns[key]?.width || 180,
      header: () => (
        <Pressable
          onPress={() => {
            handleSort(key);
            onSort?.(key, sortField === key && sortDirection === 'asc' ? 'desc' : 'asc');
          }}
          flexDirection="row"
          alignItems="center"
          justifyContent="flex-start"
          testID={`header-pressable-${key}`}
          opacity={tableColumns[key]?.sortable === false ? 0.6 : 1}
        >
          <Text size="xs" fontWeight="bold" isTruncated={singleLine}>
            {tableColumns[key]?.label}
          </Text>
          {sortField === key && tableColumns[key]?.sortable !== false && (
          <Icon
            as={sortDirection === 'asc' ? ChevronUp : ChevronDown}
            size="sm"
            ml="$1"
            color="$textSubtle"
            testID={`header-sort-icon-${key}-${sortDirection}`}
          />
          )}
        </Pressable>
      ),
      cell: ({ row }: { row: { original: AdminModelMap[K] } }) => {
        if (tableColumns[key].render) {
          const customValue = tableColumns[key].render(row.original);
          if (typeof customValue === 'string' || typeof customValue === 'number') {
            return (
              <Text size="xs" isTruncated={singleLine}>
                {String(customValue)}
              </Text>
            );
          }
          return customValue;
        }
        const value = getFieldValue(row.original, key);
        if (tableColumns[key].type === 'bool') {
          const isEditable = tableColumns[key].editable && !isActionRunning;
          return (
            <Checkbox
              value={key}
              aria-label={`status ${tableColumns[key]?.label || key}`}
              size="sm"
              isInvalid={false}
              isDisabled={!isEditable}
              isChecked={!!value}
              onChange={(newValue: boolean) => {
                if (isEditable) {
                  onAction?.('patch', { item: row.original, field: key, value: newValue });
                }
              }}
            >
              <CheckboxIndicator mr="$2">
                <CheckboxIcon as={CheckIcon} />
              </CheckboxIndicator>
            </Checkbox>
          );
        }
        if (tableColumns[key].type === 'date' && value) {
          return (
            <Text size="xs" isTruncated={singleLine}>
              {new Date(value as string).toLocaleDateString()}
            </Text>
          );
        }
        if (tableColumns[key].type === 'list' && Array.isArray(value)) {
          return (
            <Text size="xs" isTruncated={singleLine}>
              {value.join(', ')}
            </Text>
          );
        }
        const isStateColumn = key === 'state' || key.endsWith('.state') || key.includes('workflowState') || key === 'status';
        if (isStateColumn && value) {
          return <StatusBadge value={value as string} size="xs" />;
        }
        return (
          <Text size="xs" isTruncated={singleLine}>
            {value as React.ReactNode}
          </Text>
        );
      },
      meta: {
        width: tableColumns[key]?.width,
        align: tableColumns[key].type === 'bool' ? 'center' : 'left',
      },
    }));

    if (enableSelection && isSelectionMode) {
      defs.unshift({
        id: 'select',
        size: 32,
        minSize: 32,
        maxSize: 32,
        enableResizing: false,
        header: ({ table }: { table: { getIsAllRowsSelected: () => boolean; toggleAllRowsSelected: () => void } }) => (
          <Checkbox
            value="all"
            size="sm"
            isChecked={table.getIsAllRowsSelected()}
            onChange={() => table.toggleAllRowsSelected()}
            aria-label="Select all"
            isDisabled={isActionRunning}
          >
            <CheckboxIndicator
              borderColor="$borderLight400"
              sx={{ ':checked': { borderColor: '$primary600', bg: '$primary600' } }}
            >
              <CheckboxIcon as={CheckIcon} color="$white" />
            </CheckboxIndicator>
          </Checkbox>
        ),
        cell: ({ row }: { row: { id: string; getIsSelected: () => boolean; getCanSelect: () => boolean; toggleSelected: () => void } }) => (
          <Checkbox
            value={row.id}
            size="sm"
            isChecked={row.getIsSelected()}
            isDisabled={!row.getCanSelect() || isActionRunning}
            onChange={() => row.toggleSelected()}
            aria-label="Select row"
          >
            <CheckboxIndicator
              borderColor="$borderLight400"
              sx={{ ':checked': { borderColor: '$primary600', bg: '$primary600' } }}
            >
              <CheckboxIcon as={CheckIcon} color="$white" />
            </CheckboxIndicator>
          </Checkbox>
        ),
        meta: { width: 32, align: 'center', isFixed: true },
      });
    }

    if (rowActions.length > 0) {
      const iconActions = rowActions.filter(([, action]) => action.rowStyle !== 'menu');
      const menuActions = rowActions.filter(([, action]) => action.rowStyle === 'menu');
      defs.push({
        id: 'actions',
        header: () => <Text size="xs" fontWeight="$bold" isTruncated={singleLine} />,
        cell: ({ row }: { row: { original: AdminModelMap[K] } }) => (
          <HStack space="xs" justifyContent="flex-start" pl={4} alignItems="center">
            {iconActions.map(([actionCode, action]) => {
              const isAvailable = action.availCondition ? action.availCondition(row.original) : true;
              if (!isAvailable) return null;
              const isDelete = actionCode === 'delete' || actionCode === 'deleteSubmission';
              return (
                <Tooltip
                  key={actionCode}
                  placement="top"
                  trigger={(triggerProps) => (
                    <Button
                      {...triggerProps}
                      variant="outline"
                      action={isDelete ? 'negative' : 'secondary'}
                      size="xs"
                      onPress={() => onAction?.(actionCode, row.original)}
                      isDisabled={isActionRunning}
                      px="$2"
                      h={28}
                      borderColor="$borderLight"
                      sx={{
                        ':hover': { bg: isDelete ? '$statusErrorSubtle' : '$bgHover' },
                      }}
                    >
                      <Icon as={action.icon as unknown as React.ComponentType} size="xs" color={isDelete ? '$statusError' : '$textSubtle'} />
                    </Button>
                  )}
                >
                  <TooltipContent>
                    <TooltipText>{action.label || actionCode}</TooltipText>
                  </TooltipContent>
                </Tooltip>
              );
            })}
            <RowMenu
              menuActions={menuActions}
              row={row.original}
              isActionRunning={isActionRunning}
              onAction={onAction}
            />
          </HStack>
        ),
        meta: { width: menuActions.length > 0 && iconActions.length === 0 ? 56 : 120, align: 'left' },
      });
    }
    return defs as unknown as ColumnDefinition<AdminModelMap[K]>[];
  }, [
    tableColumns,
    rowActions,
    sortField,
    sortDirection,
    isActionRunning,
    enableSelection,
    isSelectionMode,
    includeDeprecated,
    handleSort,
    onSort,
    singleLine,
    onAction,
  ]);
